import {
  cloneElement,
  isValidElement,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { useLocale } from "@/locale/LocaleContext";
import styles from "./ui.module.css";

type FieldProps = {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: ReactNode;
};

export function Field({ label, htmlFor, error, hint, children }: FieldProps) {
  const errorId = `${htmlFor}-error`;
  const hintId = `${htmlFor}-hint`;
  const describedBy = [error ? errorId : null, hint && !error ? hintId : null]
    .filter(Boolean)
    .join(" ");

  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: htmlFor,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy || undefined,
      })
    : children;

  return (
    <div className={styles.field}>
      <label htmlFor={htmlFor}>{label}</label>
      {control}
      {hint && !error ? (
        <span id={hintId} className={styles.meta}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className={styles.fieldError} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={styles.input} {...props} />;
}

/** Shared textarea owner — resize-none with tall min-height / field-sizing auto-grow. */
export function TextArea({
  className,
  style,
  rows = 5,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={[styles.textarea, "resize-none", className].filter(Boolean).join(" ")}
      rows={rows}
      style={{ resize: "none", ...style }}
      {...props}
    />
  );
}

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type">;

export function PasswordInput({ className, id, ...props }: PasswordInputProps) {
  const { m } = useLocale();
  const [visible, setVisible] = useState(false);
  const fallbackId = useId();
  const inputId = id ?? fallbackId;

  return (
    <div className={styles.passwordWrap}>
      <input
        {...props}
        id={inputId}
        className={[styles.input, styles.passwordInput, className].filter(Boolean).join(" ")}
        type={visible ? "text" : "password"}
        spellCheck={false}
      />
      <button
        type="button"
        className={styles.passwordToggle}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? m.common.hidePassword : m.common.showPassword}
        aria-pressed={visible}
      >
        {visible ? m.common.hidePasswordAction : m.common.showPasswordAction}
      </button>
    </div>
  );
}
